import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, FunnelChart, Funnel, LabelList, Cell, LineChart, Line, PieChart, Pie, Legend } from 'recharts';
import { TrendingUp, Users, Target, IndianRupee, ChevronDown } from 'lucide-react';

function fmt(n) { return '₹' + (Number(n) || 0).toLocaleString('en-IN'); }
function pct(n) { return `${n || 0}%`; }

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'mtd', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
  { value: 'ytd', label: 'This Year' },
];

const COLORS = ['#0f1f3d', '#d4a017', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

function SummaryCard({ label, value, sub, color }) {
  return (
    <div className="stat-card" style={{ borderTop: `3px solid ${color || 'var(--navy)'}` }}>
      <div className="card-title">{label}</div>
      <div className="card-value" style={{ fontSize: 22 }}>{value}</div>
      {sub && <div className="card-sub">{sub}</div>}
    </div>
  );
}

// ---- Revenue Tab ----
function RevenueReport() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('mtd');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/reports/revenue', { params: { period } }).then(r => setData(r.data)),
      api.get('/reports/collection-summary').then(r => setSummary(r.data))
    ]).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      {/* All-time summary cards */}
      {summary && (
        <div className="grid grid-4 mb-24">
          <SummaryCard label="Today" value={fmt(summary.today?.total)} sub={`${summary.today?.count} transactions`} color="#10b981" />
          <SummaryCard label="This Month (MTD)" value={fmt(summary.mtd?.total)} sub={`Enroll: ${fmt(summary.mtd?.enrollment)}`} color="#3b82f6" />
          <SummaryCard label="Last Month" value={fmt(summary.last_month?.total)} sub={`Retainer: ${fmt(summary.last_month?.retainer)}`} color="#8b5cf6" />
          <SummaryCard label="This Year (YTD)" value={fmt(summary.ytd?.total)} sub={`${summary.ytd?.count} transactions`} color="#d4a017" />
        </div>
      )}

      {/* Period selector */}
      <div className="flex-between mb-16">
        <h3 style={{ fontSize: 15 }}>Revenue Breakdown</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.map(p => (
            <button key={p.value} className={`btn btn-sm ${period === p.value ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriod(p.value)}>{p.label}</button>
          ))}
        </div>
      </div>

      {data && (
        <>
          {/* Period summary */}
          <div className="grid grid-4 mb-24">
            <SummaryCard label="Total Collection" value={fmt(data.summary.totalCollection)} sub={`${data.summary.totalTransactions} txns`} color="#0f1f3d" />
            <SummaryCard label="Enrollment" value={fmt(data.summary.enrollmentCollection)} color="#10b981" />
            <SummaryCard label="Retainer" value={fmt(data.summary.retainerCollection)} color="#3b82f6" />
            <SummaryCard label="Analysis (₹49)" value={fmt(data.summary.analysisCollection)} color="#f59e0b" />
          </div>

          {/* Daily chart */}
          {data.daily.length > 0 && (
            <div className="card mb-24">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Daily Collection</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.daily}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} labelFormatter={l => `Date: ${l}`} />
                  <Bar dataKey="enrollment" name="Enrollment" fill="#10b981" stackId="a" />
                  <Bar dataKey="retainer" name="Retainer" fill="#3b82f6" stackId="a" />
                  <Bar dataKey="analysis" name="Analysis" fill="#f59e0b" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly chart for YTD */}
          {data.monthly.length > 1 && (
            <div className="card mb-24">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Monthly Trend (This Year)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.monthly}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#0f1f3d" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="enrollment" name="Enrollment" stroke="#10b981" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="retainer" name="Retainer" stroke="#3b82f6" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By consultant */}
          {data.byConsultant.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>By Consultant</h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Consultant</th><th>Total</th><th>Enrollment</th><th>Retainer</th><th>Target</th><th>Achievement</th><th>Txns</th></tr>
                  </thead>
                  <tbody>
                    {data.byConsultant.map((c, i) => {
                      const ach = c.target > 0 ? Math.round((c.total / c.target) * 100) : 0;
                      return (
                        <tr key={c.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 26, height: 26, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 700 }}>{c.name[0]}</div>
                              {c.name}
                            </div>
                          </td>
                          <td style={{ fontWeight: 700 }}>{fmt(c.total)}</td>
                          <td>{fmt(c.enrollment)}</td>
                          <td>{fmt(c.retainer)}</td>
                          <td>{fmt(c.target)}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="progress-bar" style={{ width: 80 }}>
                                <div className="progress-fill" style={{ width: `${Math.min(ach, 100)}%`, background: ach >= 100 ? 'var(--success)' : ach >= 70 ? 'var(--warning)' : 'var(--danger)' }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{ach}%</span>
                            </div>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Funnel Tab ----
function FunnelReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/funnel').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!data) return null;

  const FUNNEL_COLORS = ['#0f1f3d', '#1e3a6e', '#3b82f6', '#10b981', '#d4a017', '#ef4444'];

  return (
    <div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Funnel visualization */}
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 20 }}>Lead Conversion Funnel</h3>
          {data.funnel.map((item, i) => (
            <div key={item.stage} style={{ marginBottom: 10 }}>
              <div className="flex-between mb-4">
                <span style={{ fontSize: 13, fontWeight: 500 }}>{item.stage}</span>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{item.count}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 40, textAlign: 'right' }}>{item.pct}%</span>
                </div>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${item.pct}%`, background: FUNNEL_COLORS[i] || '#64748b' }} />
              </div>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--gold-pale)', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)' }}>{data.overallConversion}%</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Overall Conversion Rate</div>
          </div>
        </div>

        {/* Stage breakdown pie */}
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 16 }}>Stage Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.stageBreakdown.filter(s => s.count > 0)} dataKey="count" nameKey="stage" cx="50%" cy="50%" outerRadius={100} label={({ stage, count }) => count > 0 ? `${count}` : ''}>
                {data.stageBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, name) => [v, name]} />
              <Legend formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-3 mb-24">
        {/* By Source */}
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 16 }}>By Lead Source</h3>
          <table style={{ width: '100%' }}>
            <thead><tr><th>Source</th><th>Total</th><th>Enrolled</th><th>Conv.%</th></tr></thead>
            <tbody>
              {data.bySource.map(s => (
                <tr key={s.source}>
                  <td style={{ fontSize: 13 }}>{s.source}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{s.total}</td>
                  <td style={{ fontSize: 13 }}>{s.enrolled}</td>
                  <td><span className={`badge ${s.conversionRate >= 30 ? 'badge-green' : s.conversionRate >= 15 ? 'badge-yellow' : 'badge-red'}`}>{s.conversionRate}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* By Employment */}
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 16 }}>By Employment Type</h3>
          <table style={{ width: '100%' }}>
            <thead><tr><th>Type</th><th>Total</th><th>Enrolled</th><th>Conv.%</th></tr></thead>
            <tbody>
              {data.byEmployment.map(e => (
                <tr key={e.type}>
                  <td style={{ fontSize: 13 }}>{e.type}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{e.total}</td>
                  <td style={{ fontSize: 13 }}>{e.enrolled}</td>
                  <td><span className={`badge ${e.conversionRate >= 30 ? 'badge-green' : e.conversionRate >= 15 ? 'badge-yellow' : 'badge-red'}`}>{e.conversionRate}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* By Liability */}
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 16 }}>By Liability Bracket</h3>
          <table style={{ width: '100%' }}>
            <thead><tr><th>Bracket</th><th>Total</th><th>Enrolled</th><th>Conv.%</th></tr></thead>
            <tbody>
              {data.byLiability.map(l => (
                <tr key={l.label}>
                  <td style={{ fontSize: 12 }}>{l.label}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{l.total}</td>
                  <td style={{ fontSize: 13 }}>{l.enrolled}</td>
                  <td><span className={`badge ${l.conversionRate >= 30 ? 'badge-green' : l.conversionRate >= 15 ? 'badge-yellow' : 'badge-red'}`}>{l.conversionRate}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Employee Performance Tab ----
function EmployeePerformance() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('mtd');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/reports/employee', { params: { period } }).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!data) return null;

  return (
    <div>
      <div className="flex-between mb-16">
        <h3 style={{ fontSize: 15 }}>Employee Performance</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.slice(0, 4).map(p => (
            <button key={p.value} className={`btn btn-sm ${period === p.value ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriod(p.value)}>{p.label}</button>
          ))}
        </div>
      </div>

      {data.performance.map((emp, i) => {
        const ach = Math.min(emp.achievement, 100);
        const achColor = emp.achievement >= 100 ? 'var(--success)' : emp.achievement >= 70 ? 'var(--warning)' : 'var(--danger)';
        return (
          <div key={emp.id} className="card mb-16">
            <div className="flex-between mb-16">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 16 }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : emp.name[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{emp.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{emp.role}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>₹{(emp.totalCollection).toLocaleString('en-IN')}</div>
                <div style={{ fontSize: 12, color: achColor, fontWeight: 600 }}>{emp.achievement}% of target</div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="progress-bar mb-16" style={{ height: 8 }}>
              <div className="progress-fill" style={{ width: `${ach}%`, background: achColor }} />
            </div>

            {/* Stats grid */}
            <div className="grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
              {[
                { label: 'Enrollment', value: `₹${(emp.enrollCollection/1000).toFixed(1)}k` },
                { label: 'Retainer', value: `₹${(emp.retainerCollection/1000).toFixed(1)}k` },
                { label: 'Total Leads', value: emp.totalLeads },
                { label: 'Enrolled', value: emp.enrolled, color: 'var(--success)' },
                { label: 'Conv. Rate', value: `${emp.conversionRate}%`, color: emp.conversionRate >= 20 ? 'var(--success)' : 'var(--warning)' },
                { label: 'Active Clients', value: emp.enrolledClients, color: 'var(--success)' },
                { label: '🔴 Drops', value: emp.terminations || 0, color: emp.terminations > 0 ? 'var(--danger)' : undefined },
                { label: 'Drop Rate', value: `${emp.dropRate || 0}%`, color: (emp.dropRate || 0) > 10 ? 'var(--danger)' : 'var(--text-main)' },
                { label: 'Expected Retainers', value: emp.expectedRetainers || 0 },
                { label: 'Follow-ups', value: emp.followUpsScheduled },
                { label: 'Overdue FU', value: emp.overdueFollowUps, color: emp.overdueFollowUps > 0 ? 'var(--danger)' : undefined },
                { label: 'Hot Leads', value: emp.hotLeads, color: 'var(--warning)' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '10px 6px', background: 'var(--surface)', borderRadius: 7 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: s.color || 'var(--navy)' }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {data.performance.length === 0 && (
        <div className="empty-state"><Users size={40} /><h3>No employee data found</h3></div>
      )}
    </div>
  );
}

// ---- Main Reports Page ----
export default function ReportsPage() {
  const [tab, setTab] = useState('revenue');

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <h2 style={{ fontSize: 18 }}>Reports & Analytics</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {['leads','payments','performance'].map(type => (
            <a key={type}
              href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/export/${type}?token=${localStorage.getItem('crm_token')}`}
              className="btn btn-ghost btn-sm" download>
              ⬇️ {type.charAt(0).toUpperCase() + type.slice(1)} CSV
            </a>
          ))}
        </div>
      </div>

      <div className="tabs">
        {[
          { key: 'revenue', label: '💰 Revenue' },
          { key: 'funnel', label: '📊 Funnel' },
          { key: 'employee', label: '👥 Employee Performance' },
        ].map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'revenue' && <RevenueReport />}
      {tab === 'funnel' && <FunnelReport />}
      {tab === 'employee' && <EmployeePerformance />}
    </div>
  );
}
